import React, { useState, useEffect } from 'react';
import type { DiaryItem, Activity, SubType, Supplement, Medication } from '../models/diary';
import FitnessItemForm from './FitnessItemForm';
import SupplementItemForm from './SupplementItemForm';
import MedicationItemForm from './MedicationItemForm';

interface DiaryItemCreateModalProps {
  open: boolean;
  onSave: (newItem: Partial<DiaryItem>) => void;
  onCancel: () => void;
  defaultDayOfWeek?: string;
  defaultItemType?: 'FitnessItem' | 'SupplementItem' | 'MedicationItem';
  editingItem?: DiaryItem | null; // If provided, modal is in edit mode
  mode?: 'create' | 'edit'; // Explicit mode flag
  patientUserId?: string | number; // User ID to fetch patient-specific activities/supplements
}

const ALL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DiaryItemCreateModal: React.FC<DiaryItemCreateModalProps> = ({
  open,
  onSave,
  onCancel,
  defaultDayOfWeek = 'Monday',
  defaultItemType = 'FitnessItem',
  editingItem = null,
  mode = 'create',
  patientUserId
}) => {
  const isEditMode = mode === 'edit' || editingItem !== null;

  const getDefaultDays = (type: string) => (type === 'SupplementItem' || type === 'MedicationItem') ? [...ALL_DAYS] : [defaultDayOfWeek];

  const [itemType, setItemType] = useState<'FitnessItem' | 'SupplementItem' | 'MedicationItem'>(
    editingItem?.type as 'FitnessItem' | 'SupplementItem' | 'MedicationItem' || defaultItemType
  );
  const [formData, setFormData] = useState({
    name: editingItem?.name || '',
    selectedDays: editingItem ? [editingItem.dayOfWeek] : getDefaultDays(defaultItemType),
    timeOfDay: editingItem?.timeOfDay || '08:00',
    subtype: editingItem?.subtype || 'gym' as SubType,
    hidden: editingItem?.hidden || false,
    activities: editingItem?.activities || [] as Activity[],
    supplements: editingItem?.supplements || [] as Supplement[],
    medications: editingItem?.medications || [] as Medication[],
    cancellationDate: editingItem?.cancellationDate || ''
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      if (isEditMode && editingItem) {
        // Edit mode: populate form with existing item data
        setItemType(editingItem.type as 'FitnessItem' | 'SupplementItem' | 'MedicationItem');
        setFormData({
          name: editingItem.name,
          selectedDays: [editingItem.dayOfWeek],
          timeOfDay: editingItem.timeOfDay,
          subtype: editingItem.subtype,
          hidden: editingItem.hidden || false,
          activities: editingItem.activities || [],
          supplements: editingItem.supplements || [],
          medications: editingItem.medications || [],
          cancellationDate: editingItem.cancellationDate || ''
        });
      } else {
        // Create mode: reset form
        setItemType(defaultItemType);
        const defaultSubtype = defaultItemType === 'FitnessItem' ? 'gym' : defaultItemType === 'MedicationItem' ? 'medication' : 'supplementation';
        setFormData({
          name: '',
          selectedDays: getDefaultDays(defaultItemType),
          timeOfDay: '08:00',
          subtype: defaultSubtype as SubType,
          hidden: false,
          activities: [],
          supplements: [],
          medications: [],
          cancellationDate: ''
        });
      }
    }
  }, [open, defaultDayOfWeek, defaultItemType, isEditMode, editingItem]);

  // Auto-set subtype and default days when itemType changes
  useEffect(() => {
    if (itemType === 'SupplementItem' && formData.subtype !== 'supplementation') {
      setFormData(prev => ({
        ...prev,
        subtype: 'supplementation' as SubType,
        ...(!isEditMode ? { selectedDays: [...ALL_DAYS] } : {})
      }));
    } else if (itemType === 'MedicationItem' && formData.subtype !== 'medication') {
      setFormData(prev => ({
        ...prev,
        subtype: 'medication' as SubType,
        ...(!isEditMode ? { selectedDays: [...ALL_DAYS] } : {})
      }));
    } else if (itemType === 'FitnessItem' && (formData.subtype === 'supplementation' || formData.subtype === 'medication')) {
      setFormData(prev => ({ ...prev, subtype: 'gym' as SubType }));
    }
  }, [itemType, formData.subtype, isEditMode]);

  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle day selection (multiple days)
  const handleDayToggle = (day: string) => {
    setFormData(prev => {
      const selectedDays = prev.selectedDays.includes(day)
        ? prev.selectedDays.filter(d => d !== day)
        : [...prev.selectedDays, day];
      return { ...prev, selectedDays };
    });
  };

  // Simple handlers for child components
  const handleActivitiesChange = (activities: Activity[]) => {
    setFormData(prev => ({ ...prev, activities }));
  };

  const handleSupplementsChange = (supplements: Supplement[]) => {
    setFormData(prev => ({ ...prev, supplements }));
  };

  const handleSupplementNameChange = (name: string) => {
    setFormData(prev => ({ ...prev, name }));
  };

  const handleMedicationsChange = (medications: Medication[]) => {
    setFormData(prev => ({ ...prev, medications }));
  };

  const handleMedicationNameChange = (name: string) => {
    setFormData(prev => ({ ...prev, name }));
  };

  const handleSave = () => {
    // Validate that at least one day is selected
    if (formData.selectedDays.length === 0) {
      alert('Por favor selecciona al menos un día de la semana');
      return;
    }

    // Validate activities if FitnessItem
    if (itemType === 'FitnessItem' && formData.activities.length > 0) {
      const invalidActivities = formData.activities.filter(activity => {
        // Activity is valid if it has activityTypeId (new) OR activityType (existing)
        const hasTypeId = !!activity.activityTypeId;
        const hasTypeObject = activity.activityType && typeof activity.activityType === 'object';
        return !hasTypeId && !hasTypeObject;
      });
      if (invalidActivities.length > 0) {
        alert('Por favor selecciona el tipo de actividad para todas las actividades');
        return;
      }
    }

    // Validate supplements if SupplementItem
    if (itemType === 'SupplementItem') {
      const supplement = formData.supplements[0];
      if (!supplement || !supplement.supplementCatalog) {
        alert('Por favor selecciona el suplemento del catálogo');
        return;
      }
    }

    // Validate medications if MedicationItem
    if (itemType === 'MedicationItem') {
      const medication = formData.medications[0];
      if (!medication || !(medication as any).medicationCatalog) {
        alert('Por favor selecciona el medicamento del catálogo');
        return;
      }
    }

    if (isEditMode && editingItem) {
      // Edit mode: update single item
      const updatedItem: Partial<DiaryItem> = {
        ...formData,
        id: editingItem.id,
        dayOfWeek: formData.selectedDays[0], // Use first selected day for edit
        type: itemType
      };
      // Remove selectedDays from the item since it's not part of DiaryItem interface
      const { selectedDays, ...itemWithoutSelectedDays } = updatedItem as any;
      onSave(itemWithoutSelectedDays);
    } else {
      // Create mode: create one DiaryItem for each selected day
      formData.selectedDays.forEach(day => {
        const newItem: Partial<DiaryItem> = {
          ...formData,
          dayOfWeek: day,
          type: itemType
        };
        // Remove selectedDays from the item since it's not part of DiaryItem interface
        const { selectedDays, ...itemWithoutSelectedDays } = newItem as any;
        onSave(itemWithoutSelectedDays);
      });
    }
  };

  const handleDelete = () => {
    if (!window.confirm('¿Estás seguro que deseas eliminar este elemento?')) {
      return;
    }

    if (isEditMode && editingItem) {
      // Delete mode: set hidden to true and save immediately
      const updatedItem: Partial<DiaryItem> = {
        ...formData,
        id: editingItem.id,
        dayOfWeek: formData.selectedDays[0],
        type: itemType,
        hidden: true
      };
      // Remove selectedDays from the item since it's not part of DiaryItem interface
      const { selectedDays, ...itemWithoutSelectedDays } = updatedItem as any;
      onSave(itemWithoutSelectedDays);
    }
  };

  if (!open) return null;

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      width: '100vw', 
      height: '100vh', 
      background: 'rgba(0,0,0,0.18)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 1000 
    }}>
      <div style={{ 
        background: '#fff', 
        borderRadius: 12, 
        padding: '32px 40px', 
        width: '100%',
        maxWidth: 560, 
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 32px rgba(0,0,0,0.13)' 
      }}>
        <h2 style={{ marginTop: 0, marginBottom: 20, fontSize: 22, fontWeight: 600, color: '#1f2937' }}>
          {isEditMode
            ? `Editar ${itemType === 'FitnessItem' ? 'Ejercicio' : itemType === 'MedicationItem' ? 'Medicamento' : 'Suplemento'}`
            : `Crear ${itemType === 'FitnessItem' ? 'Ejercicio' : itemType === 'MedicationItem' ? 'Medicamento' : 'Suplemento'}`
          }
        </h2>
        
        {/* Activities Section (only for FitnessItem) - Moved to top */}
        {itemType === 'FitnessItem' && (
          <FitnessItemForm
            activities={formData.activities}
            patientUserId={patientUserId}
            onActivitiesChange={handleActivitiesChange}
            isEditMode={isEditMode}
            onDelete={handleDelete}
          />
        )}

        {/* Supplements Section (only for SupplementItem) - Moved to top */}
        {itemType === 'SupplementItem' && (
          <SupplementItemForm
            supplements={formData.supplements}
            patientUserId={patientUserId}
            onSupplementsChange={handleSupplementsChange}
            onNameChange={handleSupplementNameChange}
            isEditMode={isEditMode}
            onDelete={handleDelete}
          />
        )}

        {/* Medications Section (only for MedicationItem) */}
        {itemType === 'MedicationItem' && (
          <MedicationItemForm
            medications={formData.medications}
            patientUserId={patientUserId}
            onMedicationsChange={handleMedicationsChange}
            onNameChange={handleMedicationNameChange}
            isEditMode={isEditMode}
            onDelete={handleDelete}
          />
        )}

        {/* Basic Fields - hide name for SupplementItem/MedicationItem since it's auto-derived from catalog */}
        {itemType !== 'SupplementItem' && itemType !== 'MedicationItem' && (
          <label style={{ display: 'block', marginBottom: 18 }}>
            <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
              Nombre:
            </span>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8, lineHeight: '1.4' }}>
              Este es el nombre que el usuario verá en la agenda. Un item puede contener múltiples actividades de ejercicio.
            </div>
            <input 
              value={formData.name} 
              onChange={e => handleFieldChange('name', e.target.value)} 
              style={{ 
                width: '100%', 
                padding: '11px 14px', 
                border: '1px solid #cbd5e1', 
                borderRadius: 6,
                fontSize: 15,
                outline: 'none',
                boxSizing: 'border-box'
              }} 
              placeholder="Nombre del Item"
            />
          </label>
        )}

        {/* Days of week: checkboxes for create, dropdown for edit */}
        {isEditMode ? (
          <label style={{ display: 'block', marginBottom: 18 }}>
            <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
              Día de la semana:
            </span>
            <select 
              value={formData.selectedDays[0] || 'Monday'} 
              onChange={e => setFormData(prev => ({ ...prev, selectedDays: [e.target.value] }))}
              style={{ 
                width: '100%', 
                padding: '11px 40px 11px 14px', 
                border: '1px solid #cbd5e1', 
                borderRadius: 6,
                fontSize: 15,
                outline: 'none',
                background: '#fff url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3e%3cpath fill=\'%23374151\' d=\'M6 9L1 4h10z\'/%3e%3c/svg%3e") no-repeat',
                backgroundPosition: 'right 14px center',
                appearance: 'none' as any,
                WebkitAppearance: 'none' as any,
                MozAppearance: 'none' as any,
                boxSizing: 'border-box',
                cursor: 'pointer'
              }}
            >
              <option value="Monday">Lunes</option>
              <option value="Tuesday">Martes</option>
              <option value="Wednesday">Miércoles</option>
              <option value="Thursday">Jueves</option>
              <option value="Friday">Viernes</option>
              <option value="Saturday">Sábado</option>
              <option value="Sunday">Domingo</option>
            </select>
          </label>
        ) : (
          <div style={{ marginBottom: 18 }}>
            <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
              Días de la semana:
            </span>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: 8, 
              border: '1px solid #ccc', 
              borderRadius: 4, 
              padding: 12 
            }}>
              {[
                { value: 'Monday', label: 'Lunes' },
                { value: 'Tuesday', label: 'Martes' },
                { value: 'Wednesday', label: 'Miércoles' },
                { value: 'Thursday', label: 'Jueves' },
                { value: 'Friday', label: 'Viernes' },
                { value: 'Saturday', label: 'Sábado' },
                { value: 'Sunday', label: 'Domingo' }
              ].map(day => (
                <label key={day.value} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input 
                    type="checkbox"
                    checked={formData.selectedDays.includes(day.value)}
                    onChange={() => handleDayToggle(day.value)}
                    style={{ marginRight: 6 }}
                  />
                  <span style={{ fontSize: 14 }}>{day.label}</span>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, selectedDays: [] }))}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  fontSize: 12,
                  cursor: 'pointer',
                  padding: '2px 0',
                  textDecoration: 'underline',
                  textUnderlineOffset: 2
                }}
              >
                Deseleccionar días
              </button>
            </div>
            {formData.selectedDays.length === 0 && (
              <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>
                Selecciona al menos un día
              </div>
            )}
          </div>
        )}

        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Hora del día:
          </span>
          <input 
            type="time"
            value={formData.timeOfDay} 
            onChange={e => handleFieldChange('timeOfDay', e.target.value)}
            style={{ 
              width: '100%', 
              padding: '11px 14px', 
              border: '1px solid #cbd5e1', 
              borderRadius: 6,
              fontSize: 15,
              outline: 'none',
              boxSizing: 'border-box',
              cursor: 'pointer'
            }}
          />
        </label>

        <div style={{ marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Fecha de cancelación (opcional):
          </span>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 10, lineHeight: '1.4' }}>
            Día desde cuando el elemento ya no será visible para el usuario
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            {[
              { label: 'En 1 mes', months: 1 },
              { label: 'En 2 meses', months: 2 },
            ].map(opt => {
              const target = new Date();
              target.setMonth(target.getMonth() + opt.months);
              const targetStr = `${String(target.getDate()).padStart(2, '0')}-${String(target.getMonth() + 1).padStart(2, '0')}-${target.getFullYear()}`;
              const isActive = formData.cancellationDate === targetStr;
              return (
                <button
                  key={opt.months}
                  type="button"
                  onClick={() => handleFieldChange('cancellationDate', isActive ? '' : targetStr)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: isActive ? '2px solid #38b2ac' : '1px solid #cbd5e1',
                    borderRadius: 6,
                    background: isActive ? '#e6fffa' : '#fff',
                    color: isActive ? '#234e52' : '#374151',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => handleFieldChange('cancellationDate', '')}
              style={{
                flex: 1,
                padding: '8px 12px',
                border: !formData.cancellationDate ? '2px solid #38b2ac' : '1px solid #cbd5e1',
                borderRadius: 6,
                background: !formData.cancellationDate ? '#e6fffa' : '#fff',
                color: !formData.cancellationDate ? '#234e52' : '#374151',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              Custom
            </button>
          </div>
          <input 
            type="date"
            value={formData.cancellationDate ? new Date(formData.cancellationDate.split('-').reverse().join('-')).toISOString().split('T')[0] : ''}
            onChange={e => {
              if (e.target.value) {
                const [year, month, day] = e.target.value.split('-');
                handleFieldChange('cancellationDate', `${day}-${month}-${year}`);
              } else {
                handleFieldChange('cancellationDate', '');
              }
            }}
            style={{ 
              width: '100%', 
              padding: '11px 14px', 
              border: '1px solid #cbd5e1', 
              borderRadius: 6,
              fontSize: 15,
              outline: 'none',
              boxSizing: 'border-box',
              cursor: 'pointer'
            }}
          />
        </div>

        {/* Subtype based on item type - only show for FitnessItem (SupplementItem and MedicationItem have fixed subtypes) */}
        {itemType === 'FitnessItem' && (
          <label style={{ display: 'block', marginBottom: 18 }}>
            <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
              Tipo de ejercicio:
            </span>
            <select 
              value={formData.subtype} 
              onChange={e => handleFieldChange('subtype', e.target.value)}
              style={{ 
                width: '100%', 
                padding: '11px 40px 11px 14px', 
                border: '1px solid #cbd5e1', 
                borderRadius: 6,
                fontSize: 15,
                outline: 'none',
                background: '#fff url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3e%3cpath fill=\'%23374151\' d=\'M6 9L1 4h10z\'/%3e%3c/svg%3e") no-repeat',
                backgroundPosition: 'right 14px center',
                appearance: 'none' as any,
                WebkitAppearance: 'none' as any,
                MozAppearance: 'none' as any,
                boxSizing: 'border-box',
                cursor: 'pointer'
              }}
            >
              <option value="cardio">Cardio</option>
              <option value="gym">Gym</option>
              <option value="sport">Sport</option>
              <option value="swimming">Swimming</option>
              <option value="yoga">Yoga</option>
              <option value="pilates">Pilates</option>
              <option value="walking">Walking</option>
            </select>
          </label>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
          <button 
            onClick={onCancel} 
            style={{ 
              background: '#eee', 
              border: 'none', 
              borderRadius: 6, 
              padding: '8px 18px', 
              fontWeight: 600, 
              cursor: 'pointer' 
            }}
          >
            Cancelar
          </button>
          <button 
            onClick={handleSave} 
            disabled={formData.selectedDays.length === 0}
            style={{ 
              background: formData.selectedDays.length === 0 ? '#ccc' : '#38b2ac', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 6, 
              padding: '8px 18px', 
              fontWeight: 600, 
              cursor: formData.selectedDays.length === 0 ? 'not-allowed' : 'pointer',
              opacity: formData.selectedDays.length === 0 ? 0.6 : 1
            }}
          >
            {isEditMode 
              ? 'Guardar Cambios'
              : formData.selectedDays.length === 0 
                ? 'Selecciona días' 
                : formData.selectedDays.length === 1 
                  ? 'Crear Elemento' 
                  : `Crear ${formData.selectedDays.length} Elementos`
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiaryItemCreateModal;
